import { useCallback, useSyncExternalStore } from 'react'
import { format, getLang, setLang as switchLang, subscribeLang, type Entry, type Lang, type Params } from './core'
import { ka } from './ka'
import type { ProductUnit } from '../mock-data'

export { LANGS, LANG_NAMES, getLang, isLang, type Lang, type Params } from './core'

/** Every key the app may ask for — defined by the Georgian dictionary. */
export type Key = keyof typeof ka

/**
 * Georgian ships inside the app; every other language is its own chunk,
 * fetched the first time it is chosen (and precached by the service worker,
 * so it works offline after that). A Georgian till pays nothing for Russian.
 * Each has every key — a compile error otherwise, see ru/index.ts.
 */
const DICTS: Partial<Record<Lang, Record<Key, Entry>>> = { ka }
const LOADERS: Record<Exclude<Lang, 'ka'>, () => Promise<Record<Key, Entry>>> = {
  ru: () => import('./ru').then((m) => m.ru),
}

/** Fetches a language's dictionary if this device does not hold it yet. */
export async function loadLang(lang: Lang): Promise<void> {
  if (DICTS[lang] || lang === 'ka') return
  DICTS[lang] = await LOADERS[lang]()
}

/** Chooses this device's language: its dictionary first, then every screen.
 *  Offline with the chunk never fetched, the screen simply stays as it is. */
export async function setLang(lang: Lang): Promise<void> {
  try { await loadLang(lang) } catch { return }
  switchLang(lang)
}

/** The locale Intl formats a language's dates and times with. */
export const LOCALES: Record<Lang, string> = { ka: 'ka-GE', ru: 'ru-RU' }

/** Georgian day and month names. Chrome ships no Georgian date names: Intl
 *  with 'ka-GE' prints „Tue, Sep 29" or „M09 29, Tue" (seen 2026-09-29), so
 *  Georgian is spelled here and only another language asks Intl. */
export const KA_MONTHS = ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი']
export const KA_DAYS = ['კვირა', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი']

/** „სამშაბათი, 29 სექტემბერი 2026" / „вторник, 29 сентября 2026 г." — the
 *  weekday and the year when asked for, in this device's language. */
export function longDateText(d: Date, opts: { weekday?: boolean; year?: boolean } = {}, lang: Lang = getLang()): string {
  if (lang === 'ka') {
    const day = `${d.getDate()} ${KA_MONTHS[d.getMonth()]}${opts.year ? ` ${d.getFullYear()}` : ''}`
    return opts.weekday ? `${KA_DAYS[d.getDay()]}, ${day}` : day
  }
  return new Intl.DateTimeFormat(LOCALES[lang], {
    day: 'numeric', month: 'long',
    ...(opts.weekday ? { weekday: 'long' as const } : {}),
    ...(opts.year ? { year: 'numeric' as const } : {}),
  }).format(d)
}

/** A key in a given language — for the test and for code that must not follow the device. */
export function translate(lang: Lang, key: Key, params?: Params): string {
  const entry = DICTS[lang]?.[key] ?? ka[key]
  // a value only a newer database knows (a reason, a status) shows its key, never crashes the till
  return entry == null ? key : format(lang, entry, params)
}

/**
 * The key in this device's language, read at call time — for toasts and
 * other code outside React. A component uses `useT()` so it re-renders when
 * the language changes.
 */
export function t(key: Key, params?: Params): string {
  return translate(getLang(), key, params)
}

/** This device's language; the component re-renders when it changes. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribeLang, getLang, () => 'ka' as Lang)
}

export type T = (key: Key, params?: Params) => string

/**
 * `t` for a component: the component re-renders when the language changes,
 * and the function reads the language when it is CALLED — so a toast from a
 * callback memoised before the change still speaks the new language.
 */
export function useT(): T {
  const lang = useLang()
  // `lang` only renews the identity, so memoised children re-render too.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((key: Key, params?: Params) => translate(getLang(), key, params), [lang])
}

/** „ც", „კგ" — the unit's short form in the interface language. The printed
 *  receipt keeps UNIT_SHORT from mock-data: it is a document, not interface. */
export function unitKey(unit: ProductUnit | null | undefined): Key {
  return `unit.${unit ?? 'piece'}` as Key
}

/** „ცალი", „კგ" — the unit as a choice in a picker (UNIT_LABELS' words). */
export function unitLongKey(unit: ProductUnit | null | undefined): Key {
  return !unit || unit === 'piece' ? 'unit.pieceLong' : unitKey(unit)
}

/** „გუშინ" / „3 დღის წინ" — `openedAgo` (lib/branch-scope) in the interface
 *  language; the print and the morning mail keep the Georgian one. */
export function agoText(t: T, days: number): string {
  return days === 1 ? t('time.yesterday') : t('time.daysAgo', { n: days })
}

const two = (n: number) => String(n).padStart(2, '0')

/** 24-hour „14:05" — Georgian by hand (see numDateText), others through Intl. */
export function clockText(lang: Lang, iso: string | Date): string {
  const x = new Date(iso)
  if (lang === 'ka') return `${two(x.getHours())}:${two(x.getMinutes())}`
  return x.toLocaleTimeString(LOCALES[lang], { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * A NUMERIC date — „29.09.2026", „29.09 14:31", „29.09.2026 14:31". Georgian
 * is spelled by hand even for numbers: Chrome ships no ka-GE data, and what it
 * falls back to depends on the build — the owner's Windows Chrome printed
 * „09/29/2026, 02:31 PM" on a Z-report (2026-09-30) where a headless one gave
 * „2026-09-29 23:48". Other languages go through Intl (ru-RU has real data).
 * Documents — the Z-report, the accountant's Excel, the backup — pass 'ka'.
 */
export function numDateText(
  d: Date | string | number,
  { year = true, time = false }: { year?: boolean; time?: boolean } = {},
  lang: Lang = getLang(),
): string {
  const x = d instanceof Date ? d : new Date(d)
  if (lang === 'ka') {
    return `${two(x.getDate())}.${two(x.getMonth() + 1)}${year ? `.${x.getFullYear()}` : ''}`
      + (time ? ` ${two(x.getHours())}:${two(x.getMinutes())}` : '')
  }
  const o: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' }
  if (year) o.year = 'numeric'
  if (time) { o.hour = '2-digit'; o.minute = '2-digit'; o.hour12 = false }
  return x.toLocaleString(LOCALES[lang], o)
}
